/**
 * MP Video Stories (PDP)
 * ADDED: 2026-07-31 by MeshPlanet
 *
 * <mp-video-stories> renders a row of circular thumbnails, each one paired with
 * its own native <dialog> holding that story's video. One dialog per story keeps
 * every <video> element (and its poster) in place, so opening a story never has
 * to swap sources — it just plays the element that is already there.
 *
 * The play() call has to stay in the same task as the click that opened the
 * dialog: browsers only allow sound while the call still carries the user
 * gesture. Anything deferred (rAF, transitionend, await) loses it and the story
 * comes up silent, so showModal() and play() run back to back below. If the
 * browser rejects it anyway, the video is muted and retried so the story still
 * plays instead of sitting on a frozen poster.
 *
 * Data attributes:
 *   data-mp-vs-open="<i>"    trigger button for story i
 *   data-mp-vs-dialog="<i>"  dialog for story i
 *   data-mp-vs-close         close button inside a dialog
 *   data-start-muted         'true' to open muted (sound off until the user asks)
 */
(function () {
  'use strict';

  class MpVideoStories extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      // Shopify's video_tag does not guarantee the attribute, and without it iOS
      // Safari takes the video fullscreen instead of playing it in the modal.
      this.querySelectorAll('video').forEach((video) => {
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
      });

      this.querySelectorAll('[data-mp-vs-dialog]').forEach((dialog) => {
        // Fires for the close button, Escape and the backdrop alike, so the video
        // is reset no matter how the story was dismissed.
        dialog.addEventListener('close', () => {
          this.stop(dialog);
          this.releaseScroll();
        });

        // A click that lands on the dialog itself is a click on the backdrop —
        // the inner panel covers the whole box.
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
      });

      this.addEventListener('click', this.handleClick);
    }

    // The listeners live on this element and its own children, so they go away
    // with the subtree. Only the page-level scroll lock has to be undone — a
    // section re-render can drop the element while a story is open.
    disconnectedCallback() {
      this.releaseScroll();
    }

    handleClick = (event) => {
      const closer = event.target.closest('[data-mp-vs-close]');

      if (closer && this.contains(closer)) {
        const openDialog = closer.closest('[data-mp-vs-dialog]');
        if (openDialog?.open) openDialog.close();
        return;
      }

      const trigger = event.target.closest('[data-mp-vs-open]');
      if (!trigger || !this.contains(trigger)) return;

      this.open(trigger.getAttribute('data-mp-vs-open'));
    };

    open(index) {
      const dialog = this.querySelector(`[data-mp-vs-dialog="${index}"]`);

      if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;

      document.documentElement.setAttribute('scroll-lock', '');
      dialog.showModal();

      const video = dialog.querySelector('video');
      if (!video) return;

      video.currentTime = 0;
      video.muted = this.getAttribute('data-start-muted') === 'true';

      video.play()?.catch(() => {
        // Autoplay policy refused sound. Muted playback is always allowed, so
        // fall back to it rather than leaving the story on its poster.
        video.muted = true;
        video.play()?.catch(() => {});
      });
    }

    stop(dialog) {
      const video = dialog.querySelector('video');
      if (!video) return;

      video.pause();
      video.currentTime = 0;
    }

    releaseScroll() {
      // Another story (or another component) may still be open — only the last
      // one out unlocks the page.
      if (document.querySelector('dialog[open]')) return;
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  if (!customElements.get('mp-video-stories')) {
    customElements.define('mp-video-stories', MpVideoStories);
  }
})();
